'use client';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { getSectorColor } from '@/lib/sector-colors';
import { useResource } from '@/lib/use-resource';

interface SnapshotShape {
  current: {
    sectorWeights: Record<string, number>;
    topSector: { sector: string; weight: number };
    hhi: number;
    symbols: number;
  };
}

interface Slice {
  name: string;
  value: number;
}

const ETF_COLOR = '#475569';

function colorFor(sector: string): string {
  if (sector === 'Index ETF') return ETF_COLOR;
  return getSectorColor(sector);
}

export function SectorPie({ dataUrl = '/api/portfolio-snapshot', title = 'Sector Mix' }: { dataUrl?: string; title?: string } = {}) {
  const { data } = useResource<SnapshotShape>(dataUrl);

  if (!data) return null;

  const slices: Slice[] = Object.entries(data.current.sectorWeights)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const totalPositions = data.current.symbols;
  const top = data.current.topSector;
  const hhi = data.current.hhi;

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">{title}</h2>
        <div className="text-[10px] text-[#64748b]">
          {totalPositions} positions · top {top.sector} {(top.weight * 100).toFixed(0)}% · HHI {hhi.toFixed(2)}
        </div>
      </div>
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={1}
                  stroke="#0d1117"
                >
                  {slices.map((s) => (
                    <Cell key={s.name} fill={colorFor(s.name)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${((Number(value) || 0) * 100).toFixed(1)}%`, String(name)]}
                  contentStyle={{
                    backgroundColor: '#0a0e14',
                    border: '1px solid #334155',
                    borderRadius: 6,
                    fontSize: 12,
                    color: '#e2e8f0',
                  }}
                  itemStyle={{ color: '#e2e8f0' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5">
            {slices.map((s) => (
              <div key={s.name} className="flex items-baseline gap-2 text-[12px]">
                <span className="w-3 h-3 rounded-sm flex-none" style={{ backgroundColor: colorFor(s.name) }} />
                <span className="text-[#cbd5e1] flex-1 truncate">{s.name}</span>
                <span className="text-[#94a3b8] tabular-nums text-[11px]">{(s.value * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
